<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DurationInput from 'dashboard/components-next/input/DurationInput.vue';
import {
  DELAYED_TRIGGERS,
  MAX_DELAY_MINUTES,
  MIN_DELAY_MINUTES,
  WAIT_STATUSES,
  delayParts,
} from '../delayedAutomation';

const props = defineProps({
  enabled: { type: Boolean, default: false },
  trigger: { type: String, required: true },
  error: { type: String, default: '' },
});

const emit = defineEmits(['toggle', 'changeTrigger']);

const status = defineModel('status', { type: String, required: true });
const delay = defineModel('delay', { type: Number, default: null });
const unit = defineModel('unit', { type: String, required: true });

const { t } = useI18n();

const MINUTES_PER_UNIT = { minutes: 1, hours: 60, days: 24 * 60 };

// The input shows whole units, so the wait must be at least one of them.
const minDelay = computed(() =>
  Math.max(MIN_DELAY_MINUTES, MINUTES_PER_UNIT[unit.value])
);

const durationLabel = computed(() => {
  if (!delay.value) return '—';
  const { unit: delayUnit, count } = delayParts(delay.value);
  return t(`AUTOMATION.WAIT.DURATION.${delayUnit.toUpperCase()}`, { count });
});

const explanation = computed(() =>
  t(`AUTOMATION.WAIT.EXPLANATION.${props.trigger.toUpperCase()}`, {
    duration: durationLabel.value,
    status: t(`AUTOMATION.WAIT.STATUSES.${status.value.toUpperCase()}`),
  })
);
</script>

<template>
  <section class="mb-6">
    <label>{{ $t('AUTOMATION.WAIT.RUN_LABEL') }}</label>
    <div class="flex gap-2 mb-3">
      <NextButton
        sm
        :solid="!enabled"
        :faded="enabled"
        :blue="!enabled"
        :slate="enabled"
        icon="i-lucide-zap"
        :label="$t('AUTOMATION.WAIT.INSTANT')"
        @click="emit('toggle', false)"
      />
      <NextButton
        sm
        :solid="enabled"
        :faded="!enabled"
        :blue="enabled"
        :slate="!enabled"
        icon="i-lucide-hourglass"
        :label="$t('AUTOMATION.WAIT.DELAYED')"
        @click="emit('toggle', true)"
      />
    </div>
    <div
      v-if="enabled"
      class="w-full p-4 border border-solid rounded-lg bg-n-slate-2 dark:bg-n-solid-2 border-n-strong"
    >
      <div class="flex flex-wrap gap-4">
        <label class="flex-1 min-w-[14rem]">
          {{ $t('AUTOMATION.WAIT.TRIGGER_LABEL') }}
          <select
            :value="trigger"
            class="m-0"
            @change="emit('changeTrigger', $event.target.value)"
          >
            <option
              v-for="item in DELAYED_TRIGGERS"
              :key="item.key"
              :value="item.key"
            >
              {{ $t(`AUTOMATION.WAIT.TRIGGERS.${item.key.toUpperCase()}`) }}
            </option>
          </select>
        </label>
        <label v-if="trigger === 'conversation_status'" class="w-40">
          {{ $t('AUTOMATION.WAIT.STATUS_LABEL') }}
          <select v-model="status" class="m-0">
            <option v-for="item in WAIT_STATUSES" :key="item" :value="item">
              {{ $t(`AUTOMATION.WAIT.STATUSES.${item.toUpperCase()}`) }}
            </option>
          </select>
        </label>
      </div>
      <div class="flex flex-wrap gap-4 mt-4">
        <label class="w-72" :class="{ error }">
          {{ $t('AUTOMATION.WAIT.DELAY_LABEL') }}
          <div class="flex items-center gap-2">
            <DurationInput
              v-model="delay"
              v-model:unit="unit"
              :min="minDelay"
              :max="MAX_DELAY_MINUTES"
            />
          </div>
          <span v-if="error" class="message">
            {{ $t('AUTOMATION.WAIT.DELAY_ERROR') }}
          </span>
        </label>
      </div>
      <p class="mt-3 mb-0 text-sm text-n-slate-11">{{ explanation }}</p>
    </div>
  </section>
</template>
